/**
 * Calculation utility functions for budget tracking
 */

/**
 * Formats a variance value as a percentage with sign, rounded to nearest integer
 * @param {number|string} variance - The variance amount
 * @returns {string} Formatted variance percentage string
 */
export const formatVariance = (variance) => {
  if (variance === null || variance === undefined || variance === '') return '-';
  const numVariance = typeof variance === 'string' ? parseFloat(variance) : variance;
  if (isNaN(numVariance)) return '-';
  const rounded = Math.round(numVariance);
  return `${rounded > 0 ? '+' : ''}${rounded}%`;
};

/**
 * Determines if a variance is positive based on whether it's income or expense
 * @param {number} variance - The variance amount
 * @param {boolean} isIncome - Whether this is an income category
 * @returns {boolean} True if variance is positive for the category type
 */
export const isPositiveVariance = (variance, isIncome) => {
  if (variance === null || variance === undefined) return false;
  return isIncome ? variance >= 0 : variance < 0;
};

/**
 * Formats a currency amount
 * @param {number} amount - The amount to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

/**
 * Formats a percentage value
 * @param {number} value - The value to format as percentage
 * @param {number} total - The total value for percentage calculation
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (value, total) => {
  if (!total || total === 0) return '0%';
  return `${((value / total) * 100).toFixed(1)}%`;
};

/**
 * Calculates trend based on current and previous values
 * @param {Object} month - Month data object containing current and previous values
 * @returns {Object} Trend calculation results
 */
export const calculateTrend = (month) => {
  const current = month.current || 0;
  const previous = month.previous || 0;
  const difference = current - previous;
  const percentage = previous !== 0 ? (difference / Math.abs(previous)) * 100 : 0;
  
  return {
    difference,
    percentage,
    isPositive: difference > 0,
    isNegative: difference < 0,
    isNeutral: difference === 0
  };
};

/**
 * Builds a period-over-period trend descriptor for a metric card.
 * Returns null when a comparison can't meaningfully be expressed
 * (no previous period, or a previous value of zero — a % change from
 * zero is undefined).
 *
 * @param {number} current - Current period value
 * @param {number} previous - Previous period value (null/undefined if none)
 * @param {boolean} positiveIsGood - Whether an increase is favourable
 *   (true for income/balance, false for expenses)
 * @param {string} label - Tooltip text, e.g. "vs last month"
 * @returns {{value: number, positiveIsGood: boolean, label: string}|null}
 */
export const getMetricTrend = (current, previous, positiveIsGood, label) => {
  if (current === null || current === undefined) return null;
  if (previous === null || previous === undefined || previous === 0) return null;
  // A $0 metric (e.g. no income yet early in the month) always reads as a
  // -100% collapse — noise, not signal. Show no trend instead.
  if (current === 0) return null;
  const value = ((current - previous) / Math.abs(previous)) * 100;
  return { value, positiveIsGood, label };
};

/**
 * Gets properties for variance chip display
 * @param {number} variance - The variance amount
 * @param {boolean} isIncome - Whether this is an income category
 * @returns {Object} Chip properties including color and label
 */
export const getVarianceChipProps = (variance, isIncome) => {
  if (variance === '-' || variance === '' || variance === null || variance === undefined) {
    return {
      color: 'default',
      label: formatVariance(variance)
    };
  }
  const numVariance = typeof variance === 'string' ? parseFloat(variance) : variance;
  const isNeutral = numVariance > -1 && numVariance < 1;
  if (isNeutral) {
    return {
      color: 'default',
      label: formatVariance(variance)
    };
  }
  const isPositive = isPositiveVariance(numVariance, isIncome);
  return {
    color: isPositive ? 'success' : 'error',
    label: formatVariance(variance)
  };
};

/**
 * Gets properties for month-over-month chip display
 * @param {number} variance - The variance amount
 * @param {boolean} isExpense - Whether this is an expense category
 * @returns {Object} Chip properties including color and label
 */
export const getMomChipProps = (variance, isExpense = false) => {
  if (variance === '-' || variance === '' || variance === null || variance === undefined) {
    return {
      color: 'default',
      label: formatVariance(variance)
    };
  }
  const numVariance = typeof variance === 'string' ? parseFloat(variance) : variance;
  const isNeutral = numVariance > -1 && numVariance < 1;
  if (isNeutral) {
    return {
      color: 'default',
      label: formatVariance(variance)
    };
  }
  const isPositive = isExpense ? numVariance < 0 : numVariance > 0;
  return {
    color: isPositive ? 'success' : 'error',
    label: formatVariance(variance)
  };
}; 